import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Briefcase,
  Camera,
  Crosshair,
  FileText,
  ImagePlus,
  LogOut,
  MapPin,
  Pencil,
  Phone,
  Play,
  Star,
  SunMoon,
  Trash2,
  User,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SERVICE_NAMES } from '@/lib/domain';
import { detectCity, getCurrentPosition } from '@/lib/geo';
import { formatDate } from '@/lib/format';
import {
  isVideoUrl,
  pickFile,
  pickMediaFiles,
  releasePreview,
  type PickedFile,
} from '@/lib/media';
import { useAuth, useUserId } from '@/components/AuthProvider';
import { useFeedback } from '@/components/Feedback';
import { ThemeSwitch } from '@/components/ThemeProvider';
import { Avatar, Lightbox, Stars } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Account.module.css';

type WorkMedia = { id: string; photo_url: string; pending?: PickedFile };
type Review = {
  rating: number;
  review_comment: string | null;
  created_at: string;
  client_name: string;
};
type Section = 'info' | 'description' | 'services' | 'media';
type InfoValues = { fullName: string; phone: string; city: string; experienceYears: string };

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const isVideo = (item: WorkMedia) =>
  item.pending ? item.pending.kind === 'video' : isVideoUrl(item.photo_url);

export default function ProviderAccount() {
  const userId = useUserId();
  const { signOut } = useAuth();
  const { toast, confirm } = useFeedback();

  // Saved values. Each section edits a draft and writes back only on save.
  const [info, setInfo] = useState<InfoValues>({
    fullName: '',
    phone: '',
    city: '',
    experienceYears: '0',
  });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [media, setMedia] = useState<WorkMedia[]>([]);
  const [rating, setRating] = useState<number | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);

  // Drafts for the section being edited.
  const [editing, setEditing] = useState<Section | null>(null);
  const [infoDraft, setInfoDraft] = useState<InfoValues>(info);
  const [pendingAvatar, setPendingAvatar] = useState<PickedFile | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [servicesDraft, setServicesDraft] = useState<string[]>([]);
  const [mediaDraft, setMediaDraft] = useState<WorkMedia[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, phone, city, avatar_url')
        .eq('id', userId)
        .maybeSingle();

      const { data: provider } = await supabase
        .from('providers')
        .select('description, experience_years, rating')
        .eq('id', userId)
        .maybeSingle();

      setInfo({
        fullName: profile?.full_name ?? '',
        phone: profile?.phone ?? '',
        city: profile?.city ?? '',
        experienceYears: String(provider?.experience_years ?? 0),
      });
      setAvatarUrl(profile?.avatar_url ?? null);
      setDescription(provider?.description ?? '');
      setRating(provider?.rating ?? null);

      const { data: providerServices } = await supabase
        .from('provider_services')
        .select('services(name)')
        .eq('provider_id', userId);

      setServices(
        (providerServices ?? [])
          .map((row: any) => row.services?.name)
          .filter((name: any) => name && SERVICE_NAMES.includes(name))
      );

      const { data: reviewRows } = await supabase
        .from('bookings')
        .select('rating, review_comment, created_at, profiles!client_id(full_name)')
        .eq('provider_id', userId)
        .eq('status', 'completed')
        .not('rating', 'is', null)
        .order('created_at', { ascending: false });

      setReviews(
        (reviewRows ?? []).map((row: any) => ({
          rating: row.rating,
          review_comment: row.review_comment ?? null,
          created_at: row.created_at,
          client_name: row.profiles?.full_name ?? 'Client',
        }))
      );

      const { data: photos } = await supabase
        .from('provider_work_photos')
        .select('id, photo_url')
        .eq('provider_id', userId)
        .order('created_at', { ascending: false });

      if (photos) {
        // Older builds saved local file:// URIs that can never resolve — prune them.
        const valid = photos.filter((row: any) => row.photo_url?.startsWith('http'));
        const invalidIds = photos
          .filter((row: any) => !row.photo_url?.startsWith('http'))
          .map((row: any) => row.id);
        if (invalidIds.length > 0) {
          await supabase.from('provider_work_photos').delete().in('id', invalidIds);
        }
        setMedia(valid);
      }
    } catch (err) {
      console.error('Error loading provider profile:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* ── Section lifecycle ─────────────────────────────────────────────────── */

  const startEditing = (section: Section) => {
    if (section === 'info') setInfoDraft(info);
    if (section === 'description') setDescriptionDraft(description);
    if (section === 'services') setServicesDraft(services);
    if (section === 'media') setMediaDraft(media);
    setEditing(section);
  };

  const cancelEditing = () => {
    if (editing === 'info' && pendingAvatar) {
      releasePreview(pendingAvatar.url);
      setPendingAvatar(null);
    }
    if (editing === 'media') {
      mediaDraft.forEach((item) => item.pending && releasePreview(item.pending.url));
    }
    setEditing(null);
  };

  /** Writes a patch to the providers row, creating it when missing (RLS makes upsert unreliable). */
  const writeProvider = async (patch: Record<string, unknown>) => {
    const { data: existing } = await supabase
      .from('providers')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    const { error } = existing
      ? await supabase
          .from('providers')
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq('id', userId)
      : await supabase.from('providers').insert({ id: userId, ...patch });
    if (error) throw new Error(error.message);
  };

  const runSave = async (task: () => Promise<void>, successTitle: string) => {
    if (!userId) return;
    setSaving(true);
    try {
      await task();
      setEditing(null);
      toast('success', successTitle);
    } catch (err) {
      console.error('Error saving profile section:', err);
      toast('error', 'Impossible de sauvegarder', err instanceof Error ? err.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  /* ── Personal information ──────────────────────────────────────────────── */

  const pickAvatar = async () => {
    const picked = await pickFile('image');
    if (picked) {
      releasePreview(pendingAvatar?.url);
      setPendingAvatar(picked);
    }
  };

  const useCurrentCity = async () => {
    setLocating(true);
    try {
      const coords = await getCurrentPosition();
      const detected = await detectCity(coords);
      if (!detected) {
        toast('error', 'Ville introuvable', 'Saisissez votre ville manuellement.');
        return;
      }
      setInfoDraft((draft) => ({ ...draft, city: detected }));
    } catch (err) {
      toast('error', 'Localisation indisponible', err instanceof Error ? err.message : undefined);
    } finally {
      setLocating(false);
    }
  };

  const saveInfo = () =>
    runSave(async () => {
      let finalAvatarUrl = avatarUrl;
      if (pendingAvatar) {
        const storagePath = `${userId}/avatar.${pendingAvatar.extension}`;
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(storagePath, pendingAvatar.file, {
            contentType: pendingAvatar.mimeType,
            upsert: true,
          });
        if (uploadError) throw new Error(uploadError.message);
        finalAvatarUrl = supabase.storage.from('avatars').getPublicUrl(storagePath).data.publicUrl;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: infoDraft.fullName.trim(),
          phone: infoDraft.phone.trim(),
          city: infoDraft.city.trim(),
          avatar_url: finalAvatarUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);
      if (profileError) throw new Error(profileError.message);

      await writeProvider({ experience_years: Number.parseInt(infoDraft.experienceYears, 10) || 0 });

      if (pendingAvatar) {
        releasePreview(pendingAvatar.url);
        setPendingAvatar(null);
      }
      setAvatarUrl(finalAvatarUrl);
      setInfo({
        ...infoDraft,
        fullName: infoDraft.fullName.trim(),
        phone: infoDraft.phone.trim(),
        city: infoDraft.city.trim(),
        experienceYears: String(Number.parseInt(infoDraft.experienceYears, 10) || 0),
      });
    }, 'Informations mises à jour');

  /* ── Description ───────────────────────────────────────────────────────── */

  const saveDescription = () =>
    runSave(async () => {
      await writeProvider({ description: descriptionDraft.trim() });
      setDescription(descriptionDraft.trim());
    }, 'Description mise à jour');

  /* ── Areas of expertise ────────────────────────────────────────────────── */

  const toggleService = (name: string) => {
    setServicesDraft((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    );
  };

  const saveServices = () =>
    runSave(async () => {
      const { error: deleteError } = await supabase
        .from('provider_services')
        .delete()
        .eq('provider_id', userId);
      if (deleteError) throw new Error(deleteError.message);

      if (servicesDraft.length > 0) {
        const { data: serviceRows } = await supabase
          .from('services')
          .select('id, name')
          .in('name', servicesDraft);

        if (serviceRows && serviceRows.length > 0) {
          const { error } = await supabase
            .from('provider_services')
            .insert(serviceRows.map((row: any) => ({ provider_id: userId, service_id: row.id })));
          if (error) throw new Error(error.message);
        }
      }
      setServices(servicesDraft);
    }, 'Domaines d’expertise mis à jour');

  /* ── Photos and videos ─────────────────────────────────────────────────── */

  const addMedia = async () => {
    const picked = await pickMediaFiles();
    if (picked.length === 0) return;

    const accepted: PickedFile[] = [];
    let rejected = 0;
    picked.forEach((file) => {
      const limit = file.kind === 'video' ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
      if (file.file.size > limit) {
        rejected += 1;
        releasePreview(file.url);
      } else {
        accepted.push(file);
      }
    });

    if (rejected > 0) {
      toast(
        'error',
        `${rejected} fichier${rejected > 1 ? 's' : ''} trop lourd${rejected > 1 ? 's' : ''}`,
        'Maximum 10 Mo par photo et 50 Mo par vidéo.'
      );
    }

    const stamp = Date.now();
    setMediaDraft((current) => [
      ...accepted.map((file, index) => ({
        id: `pending-${stamp}-${index}`,
        photo_url: file.url,
        pending: file,
      })),
      ...current,
    ]);
  };

  const removeMediaDraft = (item: WorkMedia) => {
    if (item.pending) releasePreview(item.pending.url);
    setMediaDraft((current) => current.filter((entry) => entry.id !== item.id));
  };

  const saveMedia = () =>
    runSave(async () => {
      // Deletions: saved items no longer in the draft.
      const keptIds = new Set(mediaDraft.map((item) => item.id));
      const removedIds = media.filter((item) => !keptIds.has(item.id)).map((item) => item.id);
      if (removedIds.length > 0) {
        const { error } = await supabase
          .from('provider_work_photos')
          .delete()
          .in('id', removedIds);
        if (error) throw new Error(error.message);
      }

      // Uploads, one by one so a single failure does not lose the others.
      let failed = 0;
      const next: WorkMedia[] = [];
      for (const item of mediaDraft) {
        if (!item.pending) {
          next.push(item);
          continue;
        }
        const file = item.pending;
        const storagePath = `${userId}/${Date.now()}-${item.id}.${file.extension}`;
        const { error: uploadError } = await supabase.storage
          .from('work-photos')
          .upload(storagePath, file.file, { contentType: file.mimeType, upsert: false });
        if (uploadError) {
          console.error('Upload error:', uploadError.message);
          failed += 1;
          continue;
        }
        const publicUrl = supabase.storage.from('work-photos').getPublicUrl(storagePath).data
          .publicUrl;
        const { data: inserted, error: insertError } = await supabase
          .from('provider_work_photos')
          .insert({ provider_id: userId, photo_url: publicUrl })
          .select('id')
          .single();
        if (insertError || !inserted) {
          failed += 1;
          continue;
        }
        releasePreview(file.url);
        next.push({ id: inserted.id, photo_url: publicUrl });
      }

      setMedia(next);
      if (failed > 0) {
        toast(
          'error',
          `${failed} fichier${failed > 1 ? 's' : ''} non envoyé${failed > 1 ? 's' : ''}`,
          'Vérifiez le format (JPG, PNG, WebP, MP4, WebM, MOV) et réessayez.'
        );
      }
    }, 'Photos et vidéos mises à jour');

  /* ── Misc ──────────────────────────────────────────────────────────────── */

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Se déconnecter ?',
      confirmLabel: 'Déconnexion',
      destructive: true,
    });
    if (ok) await signOut();
  };

  if (loading) return <Spinner block label="Chargement du profil..." />;

  const sectionProps = (section: Section, onSave: () => void) => ({
    editing: editing === section,
    locked: editing !== null && editing !== section,
    saving,
    onEdit: () => startEditing(section),
    onCancel: cancelEditing,
    onSave,
  });

  const shownMedia = editing === 'media' ? mediaDraft : media;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.avatarRing}>
          <Avatar
            name={info.fullName}
            src={pendingAvatar?.url ?? avatarUrl}
            size={86}
            tone="orange"
          />
        </span>
        <h1 className={styles.name}>{info.fullName || 'Prestataire'}</h1>
        <p className={styles.headerMeta}>
          {rating !== null ? (
            <>
              <Star size={16} color="var(--brand-ink)" fill="var(--brand-ink)" />
              <span className={styles.ratingText}>{Number(rating).toFixed(1)}</span>
            </>
          ) : (
            <span className={styles.ratingText}>Sans avis</span>
          )}
          <span className={styles.certified}>Prestataire certifié</span>
        </p>
      </header>

      <div className={styles.body}>
        <div className={styles.column}>
          <EditableSection
            title="Informations personnelles"
            icon={<User size={18} />}
            {...sectionProps('info', () => void saveInfo())}
          >
            {editing === 'info' ? (
              <div className={styles.form}>
                <div className={styles.avatarRow}>
                  <Avatar
                    name={infoDraft.fullName}
                    src={pendingAvatar?.url ?? avatarUrl}
                    size={56}
                    tone="orange"
                  />
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    onClick={() => void pickAvatar()}
                    disabled={saving}
                  >
                    <Camera size={15} /> Changer la photo
                  </button>
                </div>

                <label className="field">
                  <span className="label">Nom complet</span>
                  <input
                    className="input"
                    value={infoDraft.fullName}
                    onChange={(event) =>
                      setInfoDraft((draft) => ({ ...draft, fullName: event.target.value }))
                    }
                    disabled={saving}
                  />
                </label>

                <label className="field">
                  <span className="label">Téléphone</span>
                  <input
                    className="input"
                    type="tel"
                    value={infoDraft.phone}
                    onChange={(event) =>
                      setInfoDraft((draft) => ({ ...draft, phone: event.target.value }))
                    }
                    placeholder="+212 6XX XXX XXX"
                    disabled={saving}
                  />
                </label>

                <div className="field">
                  <label className="label" htmlFor="account-city">
                    Ville
                  </label>
                  <input
                    id="account-city"
                    className="input"
                    value={infoDraft.city}
                    onChange={(event) =>
                      setInfoDraft((draft) => ({ ...draft, city: event.target.value }))
                    }
                    placeholder="Votre ville"
                    disabled={saving || locating}
                  />
                  <button
                    type="button"
                    className={styles.locationBtn}
                    onClick={() => void useCurrentCity()}
                    disabled={saving || locating}
                  >
                    {locating ? <Spinner size={14} color="var(--ink)" /> : <Crosshair size={15} />}
                    {locating ? 'Détection en cours...' : 'Utiliser ma position actuelle'}
                  </button>
                </div>

                <label className="field">
                  <span className="label">Années d&apos;expérience</span>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    value={infoDraft.experienceYears}
                    onChange={(event) =>
                      setInfoDraft((draft) => ({ ...draft, experienceYears: event.target.value }))
                    }
                    disabled={saving}
                  />
                </label>
              </div>
            ) : (
              <>
                <Field icon={<User size={16} />} label="Nom complet" value={info.fullName} />
                <Field icon={<Phone size={16} />} label="Téléphone" value={info.phone} />
                <Field icon={<MapPin size={16} />} label="Ville" value={info.city} />
                <Field
                  icon={<Briefcase size={16} />}
                  label="Années d'expérience"
                  value={`${info.experienceYears || '0'} ans`}
                />
              </>
            )}
          </EditableSection>

          <EditableSection
            title="Description"
            icon={<FileText size={18} />}
            {...sectionProps('description', () => void saveDescription())}
          >
            {editing === 'description' ? (
              <textarea
                className="textarea"
                value={descriptionDraft}
                onChange={(event) => setDescriptionDraft(event.target.value)}
                placeholder="Décrivez votre expérience, vos compétences et ce qui vous distingue..."
                disabled={saving}
                autoFocus
              />
            ) : (
              <p className={description ? styles.description : styles.hint}>
                {description || 'Aucune description renseignée.'}
              </p>
            )}
          </EditableSection>

          <EditableSection
            title="Domaines d'expertise"
            icon={<Briefcase size={18} />}
            {...sectionProps('services', () => void saveServices())}
          >
            {editing === 'services' ? (
              <>
                <p className={styles.hint}>
                  Vous recevez les demandes des services sélectionnés.
                </p>
                <div className={styles.chipRow}>
                  {SERVICE_NAMES.map((name) => {
                    const active = servicesDraft.includes(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        className={active ? 'chip chip--on' : 'chip'}
                        onClick={() => toggleService(name)}
                        aria-pressed={active}
                        disabled={saving}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : services.length > 0 ? (
              <div className={styles.chipRow}>
                {services.map((name) => (
                  <span key={name} className={styles.serviceTag}>
                    {name}
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.hint}>
                Aucun service sélectionné : vous ne recevez pas encore de demandes.
              </p>
            )}
          </EditableSection>
        </div>

        <div className={styles.column}>
          <EditableSection
            title="Photos et vidéos de travaux"
            icon={<Camera size={18} />}
            {...sectionProps('media', () => void saveMedia())}
          >
            <div className={styles.photoGrid}>
              {editing === 'media' ? (
                <button
                  type="button"
                  className={styles.addPhoto}
                  onClick={() => void addMedia()}
                  disabled={saving}
                >
                  <ImagePlus size={24} />
                  <span>Ajouter photos ou vidéos</span>
                </button>
              ) : null}

              {shownMedia.map((item) => (
                <div
                  key={item.id}
                  className={`${styles.photoWrap} ${item.pending ? styles.photoPending : ''}`}
                >
                  {isVideo(item) ? (
                    <>
                      <video
                        className={styles.photo}
                        src={item.photo_url}
                        preload="metadata"
                        muted
                        playsInline
                        controls={editing !== 'media'}
                      />
                      {editing === 'media' ? (
                        <span className={styles.videoBadge} aria-label="Vidéo">
                          <Play size={12} fill="currentColor" />
                        </span>
                      ) : null}
                    </>
                  ) : editing === 'media' ? (
                    <img className={styles.photo} src={item.photo_url} alt="Réalisation" />
                  ) : (
                    <button
                      type="button"
                      className={styles.photoOpen}
                      onClick={() => setLightbox(item.photo_url)}
                      aria-label="Agrandir la photo"
                    >
                      <img className={styles.photo} src={item.photo_url} alt="Réalisation" />
                    </button>
                  )}

                  {editing === 'media' ? (
                    <button
                      type="button"
                      className={styles.photoDelete}
                      onClick={() => removeMediaDraft(item)}
                      aria-label={isVideo(item) ? 'Retirer la vidéo' : 'Retirer la photo'}
                      disabled={saving}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            {editing === 'media' ? (
              <p className={styles.hint}>
                JPG, PNG, WebP jusqu’à 10 Mo · MP4, WebM, MOV jusqu’à 50 Mo. Plusieurs fichiers
                possibles.
              </p>
            ) : shownMedia.length === 0 ? (
              <p className={styles.hint}>
                Aucune photo ni vidéo. Les clients choisissent souvent sur vos réalisations.
              </p>
            ) : null}
          </EditableSection>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>
              <Star size={18} /> Avis clients ({reviews.length})
            </h2>
            {reviews.length === 0 ? (
              <p className={styles.hint}>Aucun avis pour le moment</p>
            ) : (
              <ul className={styles.reviewList}>
                {reviews.map((review, index) => (
                  <li key={index} className={styles.reviewCard}>
                    <div className={styles.reviewHead}>
                      <Stars value={review.rating} size={14} />
                      <span className={styles.reviewClient}>{review.client_name}</span>
                      <span className={styles.reviewDate}>{formatDate(review.created_at)}</span>
                    </div>
                    {review.review_comment ? (
                      <p className={styles.reviewComment}>{review.review_comment}</p>
                    ) : (
                      <p className={styles.hint}>Pas de commentaire</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>
              <SunMoon size={18} /> Apparence
            </h2>
            <ThemeSwitch />
          </section>

          <button
            type="button"
            className="btn btn--danger btn--block"
            onClick={() => void handleLogout()}
          >
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

/** A profile card with its own Modifier / Annuler / Enregistrer cycle. */
function EditableSection({
  title,
  icon,
  editing,
  locked,
  saving,
  onEdit,
  onCancel,
  onSave,
  children,
}: {
  title: string;
  icon: ReactNode;
  editing: boolean;
  /** Another section is being edited. */
  locked: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`${styles.card} ${editing ? styles.cardEditing : ''}`}>
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>
          {icon} {title}
        </h2>
        {editing ? null : (
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEdit}
            disabled={locked}
            title={locked ? 'Terminez d’abord la section en cours' : undefined}
          >
            <Pencil size={14} /> Modifier
          </button>
        )}
      </header>

      {children}

      {editing ? (
        <div className={styles.cardActions}>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </button>
          <button type="button" className="btn btn--primary btn--sm" onClick={onSave} disabled={saving}>
            {saving ? <Spinner size={14} color="var(--ink)" /> : null}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function Field({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldIcon}>{icon}</span>
      <div className={styles.fieldBody}>
        <p className={styles.fieldLabel}>{label}</p>
        <p className={styles.fieldValue}>{value || '—'}</p>
      </div>
    </div>
  );
}
